import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@jeraisonne/db';
import { normalizeTutorKind, type TutorKind } from '@jeraisonne/db';

type DB = SupabaseClient<Database>;

export async function updateProfile(
  db: DB,
  userId: string,
  updates: { display_name?: string; avatar_url?: string },
) {
  const { error } = await db.from('profiles').update(updates).eq('id', userId);
  if (error) throw error;
}

export async function uploadAvatar(db: DB, userId: string, file: File): Promise<string> {
  const dotIndex = file.name.lastIndexOf('.');
  const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1) : 'jpg';
  const path = `${userId}/avatar.${ext}`;
  const { error } = await db.storage.from('avatars').upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = db.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl;
}

export type TutorProfileRow = {
  bio: string | null;
  description: string | null;
  website_url: string | null;
  tutor_kind: string | null;
};

// ⚠️ Sécurité : `anthropic_api_key` est WRITE-ONLY côté client (migration 0190 —
// REVOKE SELECT au niveau colonne). Ne JAMAIS l'ajouter à ce select : PostgREST
// renverrait « permission denied ». Pour l'état de la clé, utiliser
// `getTutorApiKeyStatus` (RPC statut), jamais la valeur brute.
export async function getTutorProfile(db: DB, userId: string): Promise<TutorProfileRow | null> {
  // `tutor_kind` (migration 0189) est postérieur aux types générés (stale,
  // cf. CLAUDE.md) → client générique pour lire cette colonne.
  const { data, error } = await (db as unknown as SupabaseClient)
    .from('tutor_profiles')
    .select('bio, description, website_url, tutor_kind')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as TutorProfileRow | null) ?? null;
}

export type TutorApiKeyStatus = {
  has_key: boolean;
  /** 4 derniers caractères de la clé (jamais la clé complète), ou null si aucune. */
  last4: string | null;
};

/**
 * Statut de la clé Anthropic du prof courant, via la RPC `tutor_api_key_status`
 * (security definer) — ne renvoie JAMAIS la clé brute (write-only, migration 0190).
 */
export async function getTutorApiKeyStatus(db: DB): Promise<TutorApiKeyStatus> {
  const { data, error } = await (db as unknown as SupabaseClient).rpc('tutor_api_key_status');
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { has_key?: boolean; last4?: string | null }
    | null
    | undefined;
  return { has_key: Boolean(row?.has_key), last4: row?.last4 ?? null };
}

export async function updateTutorProfile(
  db: DB,
  userId: string,
  updates: {
    anthropic_api_key?: string | null;
    bio?: string | null;
    description?: string | null;
    website_url?: string | null;
    tutor_kind?: TutorKind;
  },
) {
  // Upsert : la ligne tutor_profiles peut ne pas exister (tutor créé avant
  // le trigger ensure_tutor_profile, ou anomalie). Un .update() seul serait
  // un no-op silencieux dans ce cas.
  // Client générique : `tutor_kind` est postérieur aux types générés (stale).
  const { error } = await (db as unknown as SupabaseClient)
    .from('tutor_profiles')
    .upsert({ id: userId, ...updates }, { onConflict: 'id' });
  if (error) throw error;
}

export type TutorPublicProfile = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  description: string | null;
  website_url: string | null;
  tutor_kind: TutorKind;
  tutor_since: string;
};

export async function getTutorPublicProfile(
  db: DB,
  tutorId: string,
): Promise<TutorPublicProfile | null> {
  const { data, error } = await db.rpc('get_tutor_public_profile', { p_tutor_id: tutorId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : null;
  if (!row) return null;
  return {
    ...(row as Omit<TutorPublicProfile, 'tutor_kind'>),
    tutor_kind: normalizeTutorKind((row as { tutor_kind?: string | null }).tutor_kind),
  };
}

export type TutorPublicExercise = {
  id: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimated_minutes: number;
  chapter_id: string;
  chapter_title: string;
  chapter_slug: string;
  subject: 'math' | 'physique';
  level: string;
  created_at: string;
};

export async function listTutorPublicExercises(
  db: DB,
  tutorId: string,
): Promise<TutorPublicExercise[]> {
  const { data, error } = await db.rpc('list_tutor_public_exercises', { p_tutor_id: tutorId });
  if (error) throw error;
  return (data ?? []) as TutorPublicExercise[];
}
